import { Grid, Typography } from "@mui/material";
import AcxStarRating from "components/UI/AcxStarRating";
import { observer } from "mobx-react";
import Question from "models/Question";
import { Tag } from "models/Tag";
import React, { FunctionComponent, useMemo } from "react";
import theme from "Theme/AppTheme";
import { ConvoModuleUIModel } from "./ConvoModuleUIModel";
import { Answer } from "models/Answer";
import AcxQuestionDescription from "components/UI/AcxQuestionDescription";
import AcxQuestionRubric from "components/UI/AcxQuestionRubric";
import ConvoAutofillIndicator from "./ConvoAutofillIndicator";

interface OwnProps {
    question: Question;
    moduleUIModel?: ConvoModuleUIModel;
    leftBorderColor?: string;
    isDisabled?: boolean;
    isAutofilled?: boolean;
    getAnswerForQuestion: (
        question: Question,
        conversationModuleId: string | undefined,
    ) => Answer | undefined;
    setAnswerForQuestion: (
        question: Question,
        tags: Tag[],
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => Answer;
}

type Props = OwnProps;

function generateRatingLabels(tags: Tag[]) {
    const ratingLabels: Record<number, string> = {};
    tags!.forEach((value) => {
        ratingLabels[value.scoredValue ?? 0] = value.value;
    });
    return ratingLabels;
}

const ConvoStarRatingAnswer: FunctionComponent<Props> = observer((props) => {
    const answer = props.getAnswerForQuestion(
        props.question,
        props.moduleUIModel?.conversationModuleId,
    );

    const activeTag =
        (answer?.activeTags.length ?? 0) > 0
            ? answer?.activeTags[0]
            : undefined;

    const starRatingTags = props.moduleUIModel?.getTagsFormattedForStarRating(
        props.question.id,
    );

    const maxStars = starRatingTags?.length;
    const ratingLabels = useMemo(
        () => generateRatingLabels(starRatingTags ?? []),
        [starRatingTags],
    );

    const onStarRatingChange = (rating: number | null) => {
        const arg =
            rating !== null
                ? starRatingTags?.find((value) => value.scoredValue === rating)
                : undefined;

        if (arg === undefined) {
            props.setAnswerForQuestion(
                props.question,
                [],
                answer,
                props.moduleUIModel?.conversationModuleId,
            );
        } else {
            props.setAnswerForQuestion(
                props.question,
                [arg],
                answer,
                props.moduleUIModel?.conversationModuleId,
            );
        }
    };

    return (
        <Grid
            id={`question-${props.question.id}`}
            item
            xs={12}
            sx={{
                pl: props.question.parentId || props.leftBorderColor ? 2 : 0,
                borderLeft: props.leftBorderColor
                    ? `2px solid ${props.leftBorderColor}`
                    : "none",
                pt: 1,
            }}
            container
            direction={"column"}
            justifyContent={"flex-start"}
            alignItems="stretch"
        >
            <Grid
                item
                xs={12}
                container
                justifyContent={"flex-start"}
                alignItems={"center"}
                sx={{ pr: 2, pl: 3 }}
            >
                <Grid item xs={12}>
                    <Grid
                        container
                        direction={"row"}
                        justifyContent={"flex-start"}
                        alignItems={"baseline"}
                        spacing={1}
                    >
                        <Grid item>
                            <Typography
                                sx={{
                                    color: answer?.isDisputed
                                        ? theme.palette.error.main
                                        : "#1F1F1F",
                                    fontSize: "14px",
                                    letterSpacing: 0,
                                    lineHeight: "20px",
                                    fontWeight: "bold",
                                }}
                            >
                                {props.question?.questionText}
                                {props.question.required &&
                                    !answer?.isAnswered && (
                                        <span
                                            style={{
                                                color: theme.palette.error.main,
                                            }}
                                        >
                                            *
                                        </span>
                                    )}
                                <ConvoAutofillIndicator
                                    isVisible={props.isAutofilled}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                {!!props.question.rubric && (
                    <Grid item xs={12} pt={1}>
                        <AcxQuestionRubric urlString={props.question.rubric} />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <AcxQuestionDescription
                        text={props.question.helperText ?? ""}
                        isModalForTablesVariant
                    />
                </Grid>
            </Grid>
            <Grid
                item
                xs={12}
                direction={"row"}
                // wrap={"nowrap"}
                container
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
                sx={{ pl: 3, pr: 3, mb: 1, height: "100%", width: "100%" }}
            >
                <Grid item xs={12} sx={{ flexDirection: "row" }}>
                    <AcxStarRating
                        id={"ConvoStarRatingAnswer-" + props.question.id}
                        max={maxStars ?? 3}
                        iconHoverColor={theme.palette.primary.main}
                        iconFilledColor={theme.palette.primary.main}
                        ratingLabels={ratingLabels}
                        onChange={onStarRatingChange}
                        value={activeTag?.scoredValue ?? 0}
                        size={"large"}
                        disabled={props.isDisabled}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
});

export default ConvoStarRatingAnswer;
